import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Send, MessageCircle, Loader2, ChevronLeft, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { API, useAuth, formatApiErrorDetail } from "../context/AuthContext";

const POLL_MS = 3000;

export default function Messages() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en") ? "en-US" : "fr-FR";
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const targetId = params.get("to") || null;

  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(targetId);
  const [activeName, setActiveName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastTsRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/threads`);
      setThreads(data.items || []);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (otherId, sinceIso = null) => {
    if (!otherId) return;
    try {
      const url = `${API}/messages/${otherId}${sinceIso ? `?since=${encodeURIComponent(sinceIso)}` : ""}`;
      const { data } = await axios.get(url);
      setActiveName(data.other_user_name || t("messagesPg.conversation"));
      setMessages((prev) => {
        if (!sinceIso) return data.items || [];
        const existing = new Set(prev.map((m) => m.id));
        const fresh = (data.items || []).filter((m) => !existing.has(m.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      const items = data.items || [];
      if (items.length) lastTsRef.current = items[items.length - 1].created_at;
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 400) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("messagesPg.userNotFound"));
        setActiveId(null);
        setMessages([]);
      }
    }
  }, [t]);

  // Initial load and switch when targetId/activeId changes
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (activeId) {
      lastTsRef.current = null;
      setMessages([]);
      loadMessages(activeId);
    }
  }, [activeId, loadMessages]);

  // Polling
  useEffect(() => {
    const tm = setInterval(() => {
      loadThreads();
      if (activeId) loadMessages(activeId, lastTsRef.current);
    }, POLL_MS);
    return () => clearInterval(tm);
  }, [activeId, loadThreads, loadMessages]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const onSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { other_user_id: activeId, text: text.trim() });
      setText("");
      await loadMessages(activeId, lastTsRef.current);
      await loadThreads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="messages-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl font-bold text-[#0A2518] mb-6 flex items-center gap-2">
          <MessageCircle className="h-7 w-7 text-[#16A34A]" /> {t("messagesPg.title")}
        </h1>

        <div className="grid lg:grid-cols-3 gap-4 h-[70vh]">
          {/* Threads list */}
          <Card className={`p-3 border-[#E5EBE7] overflow-y-auto ${activeId ? "hidden lg:block" : "block"}`} data-testid="messages-threads">
            {threads.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#4B5563]">
                {t("messagesPg.empty")}<br />
                {user?.role === "parent" ? t("messagesPg.emptyHelpParent") : t("messagesPg.emptyHelpEducator")}
              </div>
            ) : threads.map((th) => (
              <button
                key={th.thread_id}
                onClick={() => { setActiveId(th.other_user_id); setParams({ to: th.other_user_id }); }}
                className={`w-full text-left p-3 rounded-xl mb-1 hover:bg-[#F0FDF4] transition-colors ${activeId === th.other_user_id ? "bg-[#F0FDF4] ring-1 ring-[#16A34A]/30" : ""}`}
                data-testid={`thread-${th.other_user_id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#0A2518] truncate">{th.other_user_name}</span>
                  {th.unread_count > 0 && (
                    <Badge className="bg-[#16A34A] text-white border-0 px-2 py-0 text-xs" data-testid={`unread-${th.other_user_id}`}>{th.unread_count}</Badge>
                  )}
                </div>
                <div className="text-xs text-[#4B5563] truncate mt-0.5">{th.last_text}</div>
                <div className="text-[10px] text-[#9CA3AF] mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(th.last_at).toLocaleString(locale)}</div>
              </button>
            ))}
          </Card>

          {/* Active thread */}
          <Card className={`lg:col-span-2 flex flex-col border-[#E5EBE7] ${activeId ? "flex" : "hidden lg:flex"}`} data-testid="messages-thread-pane">
            {activeId ? (
              <>
                <div className="px-5 py-3 border-b border-[#E5EBE7] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => { setActiveId(null); setParams({}); }}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="font-display font-semibold text-[#0A2518]" data-testid="messages-other-name">{activeName}</span>
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="messages-list">
                  {messages.length === 0 ? (
                    <p className="text-center text-sm text-[#4B5563]">{t("messagesPg.startConv")}</p>
                  ) : messages.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${m.mine ? "bg-[#16A34A] text-white rounded-br-md" : "bg-[#F3F4F6] text-[#0A2518] rounded-bl-md"}`} data-testid={`msg-${m.id}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <div className={`text-[10px] mt-1 ${m.mine ? "text-white/70" : "text-[#6B7280]"}`}>
                          {new Date(m.created_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={onSend} className="border-t border-[#E5EBE7] p-3 flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("messagesPg.inputPh")} className="h-11" data-testid="message-input" />
                  <Button type="submit" disabled={sending || !text.trim()} className="rounded-full h-11 px-5 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="message-send-btn">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#4B5563]">
                {t("messagesPg.selectConv")}
              </div>
            )}
          </Card>
        </div>

        {threads.length === 0 && !activeId && (
          <p className="mt-6 text-center text-sm text-[#4B5563]">
            <Link to="/educators-list" className="text-[#16A34A] font-medium hover:underline">{t("messagesPg.ctaFindEducator")}</Link>
            {t("messagesPg.ctaFindEducatorTail")}
          </p>
        )}
      </div>
    </div>
  );
}
